import { useRef, useEffect } from 'react'
import * as THREE from 'three'
import { useThree } from '@react-three/fiber'

interface LightingProps {
  preset: string
}

const LIGHTING_PRESETS: Record<string, {
  ambient: { color: string; intensity: number }
  directional: { color: string; intensity: number; position: [number, number, number] }
  pointLights: Array<{ color: string; intensity: number; position: [number, number, number]; distance?: number }>
  fog: { color: string; near: number; far: number }
  background: string
}> = {
  moonlight: {
    ambient: { color: '#1a1a2e', intensity: 0.3 },
    directional: { color: '#a0c4ff', intensity: 0.6, position: [5, 10, 5] },
    pointLights: [
      { color: '#ffd700', intensity: 0.4, position: [0, 3, 0], distance: 10 }
    ],
    fog: { color: '#0f172a', near: 10, far: 30 },
    background: '#0f172a'
  },
  
  dawn: {
    ambient: { color: '#fef3c7', intensity: 0.5 },
    directional: { color: '#ffecd2', intensity: 1.0, position: [10, 5, 0] },
    pointLights: [],
    fog: { color: '#ffecd2', near: 15, far: 40 },
    background: '#ffecd2'
  },
  
  campfire: {
    ambient: { color: '#2c1810', intensity: 0.2 },
    directional: { color: '#ff6b35', intensity: 0.5, position: [0, 2, 0] },
    pointLights: [
      { color: '#ff6b35', intensity: 1.2, position: [0, 1, 0], distance: 8 },
      { color: '#ff8c42', intensity: 0.6, position: [1, 0.5, 1], distance: 5 }
    ],
    fog: { color: '#2c1810', near: 5, far: 20 },
    background: '#2c1810'
  },
  
  festival: {
    ambient: { color: '#2d0a31', intensity: 0.4 },
    directional: { color: '#ff006e', intensity: 0.4, position: [3, 8, 3] },
    pointLights: [
      { color: '#ff006e', intensity: 0.8, position: [-3, 2, 0], distance: 8 },
      { color: '#fb5607', intensity: 0.8, position: [3, 2, 0], distance: 8 },
      { color: '#3a86ff', intensity: 0.6, position: [0, 3, -2], distance: 6 }
    ],
    fog: { color: '#2d0a31', near: 8, far: 25 },
    background: '#2d0a31'
  },
  
  dream: {
    ambient: { color: '#a18cd1', intensity: 0.3 },
    directional: { color: '#fbc2eb', intensity: 0.5, position: [0, 5, 5] },
    pointLights: [
      { color: '#ffd700', intensity: 0.3, position: [2, 2, 2], distance: 15 }
    ],
    fog: { color: '#a18cd1', near: 10, far: 35 },
    background: '#a18cd1'
  },
  
  ominous: {
    ambient: { color: '#0f2027', intensity: 0.15 },
    directional: { color: '#2d6a4f', intensity: 0.3, position: [0, 5, 0] },
    pointLights: [
      { color: '#2d6a4f', intensity: 0.5, position: [1, 0.5, 1], distance: 6 }
    ],
    fog: { color: '#0f2027', near: 8, far: 25 },
    background: '#0f2027'
  },

  // ---- 数据库绘本场景预设（对齐 seeds 中 lighting 取值）----
  morning_sun: {
    ambient: { color: '#fff8e1', intensity: 0.6 },
    directional: { color: '#fff3c4', intensity: 1.2, position: [6, 12, 4] },
    pointLights: [
      { color: '#ffe082', intensity: 0.5, position: [-4, 4, 2], distance: 12 }
    ],
    fog: { color: '#fff8e1', near: 18, far: 45 },
    background: '#fff8e1'
  },
  sunset_glow: {
    ambient: { color: '#ffcc80', intensity: 0.5 },
    directional: { color: '#ff8a65', intensity: 1.0, position: [-6, 6, 2] },
    pointLights: [
      { color: '#ff7043', intensity: 0.6, position: [2, 3, 0], distance: 10 },
      { color: '#ffd180', intensity: 0.4, position: [-2, 1, -2], distance: 8 }
    ],
    fog: { color: '#ffcc80', near: 12, far: 35 },
    background: '#ffcc80'
  },
  dreamy: {
    ambient: { color: '#e1d5ff', intensity: 0.5 },
    directional: { color: '#c3a6ff', intensity: 0.7, position: [2, 6, 4] },
    pointLights: [
      { color: '#ffffff', intensity: 0.35, position: [0, 4, 0], distance: 14 }
    ],
    fog: { color: '#e1d5ff', near: 10, far: 38 },
    background: '#e1d5ff'
  },
  dark_storm: {
    ambient: { color: '#1a2634', intensity: 0.3 },
    directional: { color: '#6b7a99', intensity: 0.25, position: [0, 8, 2] },
    pointLights: [
      { color: '#aab7d4', intensity: 0.35, position: [-1, 3, 1], distance: 9 },
      { color: '#59647a', intensity: 0.3, position: [2, 1, -1], distance: 7 }
    ],
    fog: { color: '#141d2b', near: 6, far: 24 },
    background: '#141d2b'
  },
  cave_dim: {
    ambient: { color: '#2b1f16', intensity: 0.25 },
    directional: { color: '#8a6b4f', intensity: 0.2, position: [0, 4, 0] },
    pointLights: [
      { color: '#ff9e5e', intensity: 0.6, position: [0, 0.5, 0], distance: 6 },
      { color: '#7a5c3e', intensity: 0.3, position: [-2, 1, 1], distance: 5 }
    ],
    fog: { color: '#1c1410', near: 5, far: 20 },
    background: '#1c1410'
  }
}

export default function Lighting({ preset }: LightingProps) {
  const { scene } = useThree()
  const lightsRef = useRef<THREE.Light[]>([])
  
  useEffect(() => {
    const config = LIGHTING_PRESETS[preset] || LIGHTING_PRESETS.moonlight
    
    // 清除旧光源
    lightsRef.current.forEach(light => scene.remove(light))
    lightsRef.current = []
    
    // 环境光
    const ambient = new THREE.AmbientLight(config.ambient.color, config.ambient.intensity)
    scene.add(ambient)
    lightsRef.current.push(ambient)
    
    // 方向光
    const directional = new THREE.DirectionalLight(
      config.directional.color,
      config.directional.intensity
    )
    directional.position.set(...config.directional.position)
    directional.castShadow = true
    directional.shadow.mapSize.width = 1024
    directional.shadow.mapSize.height = 1024
    scene.add(directional)
    lightsRef.current.push(directional)
    
    // 点光源
    config.pointLights.forEach(pl => {
      const point = new THREE.PointLight(pl.color, pl.intensity, pl.distance || 10)
      point.position.set(...pl.position)
      scene.add(point)
      lightsRef.current.push(point)
    })
    
    // 雾效
    scene.fog = new THREE.Fog(config.fog.color, config.fog.near, config.fog.far)
    
    // 背景
    scene.background = new THREE.Color(config.background)
    
    return () => {
      lightsRef.current.forEach(light => scene.remove(light))
      lightsRef.current = []
      scene.fog = null
    }
  }, [preset, scene])
  
  return null
}
